import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * Scroll-lock robusto compartido (patrón de MenuSheet/MemberKeviaSheet) con
 * CONTADOR de locks a nivel de módulo para soportar sheets ANIDADOS.
 *
 * En iOS Safari, overflow:hidden en el body NO basta: el fondo sigue
 * scrolleándose. Se fija el body (position:fixed + top:-scrollY) y se restaura
 * el scroll al cerrar. El contador asegura que SOLO el primer sheet capture la
 * posición y fije el body, y SOLO el último (al volver a 0) restaure — así un
 * sheet anidado (p.ej. plataforma abierto desde el de asignar) no recaptura
 * scrollY=0 ni desbloquea el body mientras el padre sigue abierto.
 */
let lockCount = 0;
let savedScrollY = 0;

function lockBody() {
  if (lockCount === 0) {
    savedScrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top      = `-${savedScrollY}px`;
    document.body.style.width    = "100%";
  }
  lockCount += 1;
}

function unlockBody() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top      = "";
    document.body.style.width    = "";
    window.scrollTo(0, savedScrollY);
  }
}

/**
 * BottomSheet - Componente UX de superposición optimizado para Mobile-First.
 * Reemplaza modales centrados para flujos de selección, asignación y filtrado.
 *
 * iOS keyboard fix: uses window.visualViewport to keep the overlay within the
 * visible area when the soft keyboard is active. The container tracks vv.height
 * and vv.offsetTop so the sheet always sits above the keyboard, not behind it.
 * Fallback for browsers without visualViewport: the initial 100dvh value stays.
 */
export default function BottomSheet({
  isOpen,
  onClose,
  title,
  subtitle,
  headerNode,
  stickyHeader,
  children,
  footer,
  contentClassName = "p-4",
  compact = false,
  centered = false,
}) {
  useEffect(() => {
    if (!isOpen) return;
    lockBody();
    return () => unlockBody();
  }, [isOpen]);

  // Visual Viewport tracking — keeps overlay anchored to the visible area
  // above the iOS soft keyboard. On keyboard open, vv.height shrinks and the
  // overlay height is updated so the sheet slides up with the keyboard edge.
  // Uses direct style mutation (no state re-render) for minimal latency.
  const containerRef = useRef(null);
  useEffect(() => {
    if (!isOpen || typeof window === "undefined") return;
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      el.style.top    = `${Math.round(vv.offsetTop)}px`;
      el.style.height = `${Math.round(vv.height)}px`;
    };

    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (centered) {
    return (
      <div
        ref={containerRef}
        className="fixed left-0 right-0 z-[60] flex items-center justify-center px-4"
        style={{ top: 0, height: "100dvh" }}
      >
        {/* BACKDROP */}
        <div
          className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px] animate-in fade-in duration-200"
          onClick={onClose}
          aria-hidden="true"
        />
        {/* MODAL PANEL — max-h is % of container which JS sets to visual viewport */}
        <div
          className="relative w-full max-w-sm bg-page-bg rounded-[28px] flex flex-col shadow-2xl animate-in zoom-in-95 fade-in duration-200 ease-out max-h-[88%]"
          role="dialog"
          aria-modal="true"
        >
          {/* HEADER */}
          <div className="px-5 pt-5 pb-4 flex items-center gap-3 shrink-0">
            <div className="flex-1 min-w-0">
              {headerNode ? headerNode : (
                <>
                  {subtitle && (
                    <p className="text-[12px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5">
                      {subtitle}
                    </p>
                  )}
                  {title && (
                    <h3 className="text-[20px] font-bold text-slate-900 dark:text-slate-100 leading-none">
                      {title}
                    </h3>
                  )}
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="h-11 w-11 rounded-full bg-slate-200/50 dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 transition-colors active:bg-slate-200 dark:active:bg-slate-700 shrink-0"
              aria-label="Cerrar"
            >
              <X size={16} />
            </button>
          </div>
          {stickyHeader && <div className="shrink-0">{stickyHeader}</div>}
          <div
            className={`flex-1 overflow-y-auto overscroll-contain ${contentClassName}`}
            style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}
          >
            {children}
          </div>
          {footer && (
            <div
              className="px-4 pt-3 bg-[var(--kv-card-bg)] border-t border-slate-200/50 dark:border-slate-700/50 shrink-0"
              style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed left-0 right-0 z-[60] flex flex-col justify-end"
      style={{ top: 0, height: "100dvh" }}
    >
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px] transition-opacity animate-in fade-in duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* SHEET CONTAINER — max-h/min-h use % of container so they always resolve
          against the visual viewport height (set by JS above), not the CSS viewport
          height. This prevents the sheet from being taller than the visible area
          when the keyboard is active on older iOS where dvh ≠ visual viewport. */}
      <div
        className={`relative w-full ${compact ? "min-h-0" : "min-h-[82%]"} max-h-[85%] overflow-hidden bg-page-bg rounded-t-[32px] flex flex-col shadow-2xl animate-in slide-in-from-bottom-full duration-300 ease-out`}
        role="dialog"
        aria-modal="true"
      >
        {/* GRIPPER */}
        <div className="w-full flex justify-center pt-3 pb-1 shrink-0">
          <div className="h-1.5 w-12 bg-slate-300 dark:bg-slate-600 rounded-full" />
        </div>

        {/* HEADER — flex-1 on the content side ensures headerNode + X align correctly */}
        <div className="px-5 pb-4 flex items-center gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            {headerNode ? (
              headerNode
            ) : (
              <>
                {subtitle && (
                  <p className="text-[12px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-0.5">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h3 className="text-[20px] font-bold text-slate-900 dark:text-slate-100 leading-none">
                    {title}
                  </h3>
                )}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="h-11 w-11 rounded-full bg-slate-200/50 dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 transition-colors active:bg-slate-200 dark:active:bg-slate-700 shrink-0"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* STICKY HEADER EXTENSION */}
        {stickyHeader && <div className="shrink-0">{stickyHeader}</div>}

        {/* SCROLLABLE CONTENT — min-h-0 overrides flex default min-height:auto so iOS Safari
            correctly creates a scroll boundary. Without it the overflow-y:auto container
            reports its min-size as full content height and refuses to scroll.
            Bottom clearance is expressed as a child <div> spacer, not as paddingBottom on
            this element. In iOS Safari, paddingBottom on overflow-y:auto inside overflow:hidden
            is excluded from scrollHeight, causing maxScrollTop to be too small. A child element
            is always included in scrollHeight, so the full content is reachable. */}
        <div
          className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${contentClassName}`}
        >
          {children}
          <div aria-hidden="true" style={{ height: "calc(96px + env(safe-area-inset-bottom))", flexShrink: 0 }} />
        </div>

        {/* OPTIONAL STICKY FOOTER — always visible above keyboard because the
            container is bounded to the visual viewport via the JS listener above. */}
        {footer && (
          <div
            className="px-4 pt-3 bg-[var(--kv-card-bg)] border-t border-slate-200/50 dark:border-slate-700/50 shrink-0"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
